import type { SortingRule } from '@/shared/models/queryModels'
import type { SortParamsInput } from '@hello-aria/query-string/dist/stringify'
import { isEmpty } from 'lodash'

export type SortNamespace = 'default' | 'creditLimitChangeLog'

type BaseSortConfig = {
    namespace?: SortNamespace
}

type UiSortConfig = BaseSortConfig & {
    usage: 'ui'
}

type ApiSortConfig = BaseSortConfig & {
    usage: 'api'
}

export type SortConfig = UiSortConfig | ApiSortConfig

function isUiSort<T>(sorts: readonly object[]): sorts is SortingRule<T>[] {
    return sorts.length > 0 && 'id' in sorts[0]
}

function isApiSort(sorts: readonly object[]): sorts is SortParamsInput[] {
    return sorts.length > 0 && 'path' in sorts[0]
}

const SORT_DEFAULTS: Record<SortNamespace, SortParamsInput[]> = {
    default: [{ path: 'createdAt', direction: 'desc' }],
    creditLimitChangeLog: [{ path: 'date', direction: 'desc' }]
}

function convertToApiSort<T>(sorts: SortingRule<T>[]): SortParamsInput[] {
    return sorts.map((sort) => ({
        path: sort.id,
        direction: sort.desc ? 'desc' : 'asc'
    }))
}

function convertToUiSort<T>(sorts: SortParamsInput[]): SortingRule<T>[] {
    return sorts.map((sort) => ({
        id: sort.path,
        desc: sort.direction === 'desc'
    }))
}

function getDefaultSort<T>(config: SortConfig): SortingRule<T>[] | SortParamsInput[] {
    const namespace = config.namespace || 'default'
    const defaultSort = SORT_DEFAULTS[namespace]

    return config.usage === 'api' ? defaultSort : convertToUiSort(defaultSort)
}

export function mapSort<T>(sorts: SortingRule<T>[] | null | undefined, config: UiSortConfig): SortingRule<T>[]
export function mapSort(sorts: SortParamsInput[] | null | undefined, config: ApiSortConfig): SortParamsInput[]
export function mapSort<T>(
    sorts: SortingRule<T>[] | SortParamsInput[] | null | undefined,
    config: SortConfig
): SortingRule<T>[] | SortParamsInput[] {
    if (!sorts || isEmpty(sorts)) {
        return getDefaultSort(config)
    }

    if (config.usage === 'ui') {
        return isUiSort<T>(sorts) ? sorts : convertToUiSort(sorts)
    }

    return isApiSort(sorts) ? sorts : convertToApiSort(sorts)
}
